import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Upload, X, CheckCircle2, Sparkles } from "lucide-react";
import { SupportTicketApi } from "../../api/support/service";


const ticketSchema = z.object({
  category: z.string().min(1, "Please select a category"),
  priority: z.string().min(1, "Please select a priority"),
  subject: z
    .string()
    .min(5, "Subject must be at least 5 characters")
    .max(100, "Subject must be less than 100 characters"),
  description: z
    .string()
    .min(20, "Please provide more details (at least 20 characters)")
    .max(2000, "Description must be less than 2000 characters"),
  project: z.string().optional(),
});

const categories = [
  "Login & Account",
  "Projects & Sites",
  "Finance & Payments",
  "Procurement",
  "Technical Issue",
  "Feature Request",
  "Other",
];

const priorities = [
  { value: "low", label: "Low", description: "General question or feedback" },
  {
    value: "medium",
    label: "Medium",
    description: "Something isn't working as expected",
  },
  { value: "high", label: "High", description: "Major feature is broken" },
  { value: "urgent", label: "Urgent", description: "Business is blocked" },
];

const projects = [
  "The 42 - Mumbai",
  "Green Valley Villas",
  "Tech Park Phase 2",
  "Sunrise Apartments",
];

export function RaiseTicketTab({ onTicketCreated }) {
  const [attachments, setAttachments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [ticketId, setTicketId] = useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(ticketSchema),
  });

  const handleFileChange = (e) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setAttachments((prev) => [...prev, ...newFiles].slice(0, 5));
    }
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      // Get organization ID from localStorage
      const organizationId = JSON.parse(localStorage.getItem("organization"))?.id;

      if (!organizationId) {
        throw new Error("Organization not found. Please log in again.");
      }

      // Create FormData for file upload support
      const formData = new FormData();
      formData.append("org", organizationId);
      formData.append("category", data.category);
      // Capitalize first letter of priority
      formData.append("priority", data.priority.charAt(0).toUpperCase() + data.priority.slice(1));
      formData.append("issue_header", data.subject);
      formData.append("issue_description", data.description);

      // Add attachments if any
      if (attachments.length > 0) {
        attachments.forEach((file) => {
          formData.append("attachments", file);
        });
      }

      // Call the API
      const response = await SupportTicketApi.createTicket(formData);

      if (response && response.status) {
        // Extract ticket number from response
        const newTicketId = response.data?.ticket_number || `SM-${Math.floor(1000 + Math.random() * 9000)}`;
        setTicketId(newTicketId);
        setIsSuccess(true);

        if (onTicketCreated) {
          onTicketCreated(newTicketId);
        }
      } else {
        throw new Error(response.message || "Failed to create ticket");
      }
    } catch (error) {
      console.error("Error creating ticket:", error);
      alert(error.message || "Failed to create ticket. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateAnother = () => {
    setIsSuccess(false);
    setTicketId(null);
    setAttachments([]);
    reset();
  };

  // Success State
  if (isSuccess && ticketId) {
    return (
      <div className="w-full max-w-2xl mx-auto">
        <div className="text-center py-12 animate-fade-in">
          {/* Confetti particles */}
          <div className="relative mb-8 h-20">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="absolute animate-pulse"
                style={{
                  left: `calc(50% - 60px + ${i * 24}px)`,
                  animationDelay: `${i * 0.1}s`,
                }}
              >
                <Sparkles
                  className={`h-4 w-4 ${i % 3 === 0
                    ? "text-accent"
                    : i % 3 === 1
                      ? "text-secondary"
                      : "text-primary"
                    }`}
                />
              </div>
            ))}
            <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center shadow-sm">
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            Ticket created successfully 🎉
          </h2>
          <p className="text-gray-600 mb-2">
            Your ticket ID is{" "}
            <span className="font-mono font-semibold text-gray-900">
              {ticketId}
            </span>
          </p>
          <p className="text-sm text-gray-500 mb-8">
            Our team will get back to you soon.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={handleCreateAnother}
              className="inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white px-6 py-2.5 text-sm font-medium hover:bg-gray-50 transition-colors shadow-sm"
            >
              Create Another
            </button>
            <button className="inline-flex items-center justify-center rounded-xl bg-[#3B0069] text-white px-6 py-2.5 text-sm font-medium hover:bg-[#2d0051] transition-colors shadow-sm">
              View Ticket
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full animate-fade-in">
      <div className=" mx-6 mb-6">
        <div className="mb-8">
          <p className="text-gray-600 text-base">
            Tell us what's going on — we'll take care of it 💛
          </p>
          <p className="text-sm text-gray-500 mt-1">
            We usually reply within 24–48 hours.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Category */}
            <div className="space-y-2">
              <label
                htmlFor="category"
                className="text-sm font-medium text-gray-900 block"
              >
                Category
              </label>
              <select
                id="category"
                {...register("category")}
                className="w-full rounded-xl h-12 bg-white border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-shadow"
              >
                <option value="">Select category</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
              {errors.category && (
                <p className="text-sm text-red-600">
                  {errors.category.message}
                </p>
              )}
            </div>

            {/* Priority */}
            <div className="space-y-2">
              <label
                htmlFor="priority"
                className="text-sm font-medium text-gray-900 block"
              >
                Priority
              </label>
              <select
                id="priority"
                {...register("priority")}
                className="w-full rounded-xl h-12 bg-white border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-shadow"
              >
                <option value="">Select priority</option>
                {priorities.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label} — {p.description}
                  </option>
                ))}
              </select>
              {errors.priority && (
                <p className="text-sm text-red-600">
                  {errors.priority.message}
                </p>
              )}
            </div>
          </div>

          {/* Subject */}
          <div className="space-y-2">
            <label
              htmlFor="subject"
              className="text-sm font-medium text-gray-900 block"
            >
              Subject
            </label>
            <input
              id="subject"
              type="text"
              placeholder="Brief summary of your issue"
              {...register("subject")}
              className="w-full rounded-xl h-12 bg-white border border-gray-300 px-3 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-shadow"
            />
            {errors.subject && (
              <p className="text-sm text-red-600">{errors.subject.message}</p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label
              htmlFor="description"
              className="text-sm font-medium text-gray-900 block"
            >
              Description
            </label>
            <textarea
              id="description"
              placeholder="Please describe your issue in detail. Include any error messages, steps to reproduce, or screenshots that might help us understand better."
              {...register("description")}
              className="w-full rounded-xl min-h-[140px] bg-white border border-gray-300 px-3 py-3 text-sm placeholder:text-gray-400 resize-y focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-shadow"
            />
            {errors.description && (
              <p className="text-sm text-red-600">
                {errors.description.message}
              </p>
            )}
          </div>

          {/* Project (Optional) */}
          <div className="space-y-2">
          
          </div>

          {/* Attachments */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-900 block">
              Attachments
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-purple-400 transition-all duration-200">
              <input
                type="file"
                id="attachments"
                multiple
                accept="image/*,.pdf,.doc,.docx"
                onChange={handleFileChange}
                className="hidden"
              />
              <label
                htmlFor="attachments"
                className="cursor-pointer flex flex-col items-center gap-2"
              >
                <Upload className="h-8 w-8 text-gray-400" />
                <p className="text-sm text-gray-600">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-gray-500">
                  Images, PDFs, or documents (max 5 files)
                </p>
              </label>
            </div>

            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {attachments.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg text-sm animate-fade-in shadow-sm"
                  >
                    <span className="truncate max-w-[200px] text-gray-700">
                      {file.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      className="text-gray-500 hover:text-red-600 transition-colors flex-shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Email Notice */}
          <div className="bg-b-50 border border-blue-200 rounded-xl p-4 shadow-sm">
            <p className="text-sm text-gray-700 flex items-start gap-2">
              <span>
                Replies will be sent to{" "}
                <span className="font-medium text-gray-900">
                  sitemaster@company.com
                </span>
              </span>
            </p>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full h-12 rounded-xl bg-[#3B0069] hover:bg-[#2d0051] text-white font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md active:scale-[0.98]"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <span className="animate-pulse">Submitting...</span>
              </span>
            ) : (
              "Submit Ticket"
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
